import { v4 as uuid } from 'uuid';
import { assign, isEqual } from "lodash";
import { distinctUntilChanged } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import type { TableRowSelectEvent } from "primeng/table";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from "@medsurf/flat-facades";
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { ModalControlComponent } from "@medsurf/flat-components";
import { VocabularyWordDialogComponent } from "./vocabulary-word-dialog/vocabulary-word-dialog.component";

@Component({
    selector: 'medsurf-chapter-main-vocabulary',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './vocabulary.component.html',
    styleUrls: ['./vocabulary.component.scss'],
    standalone: false
})
export class VocabularyComponent {
  #isSubmitted = signal(false);
  #page: FlatModels.PageEntityModels.PageEntityType | null;
  #vocabularyGroups: FlatModels.VocabularyGroupEntityModels.VocabularyGroup[];
  vocabularyGroupFormsData: { id: string, form: FormGroup }[];
  vocabularyWords: { [id: string]: FlatModels.VocabularyWordEntityModels.VocabularyWord[] };
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public vocabularySharedFacade: FlatFacades.VocabularySharedFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
    // Update Page
    this.vocabularySharedFacade.currentSelectedPage$.pipe(takeUntilDestroyed()).subscribe((page) => {
      this.#page = page;
    });
    
    // Update Vocabulary Groups
    this.vocabularySharedFacade.currentSelectedVocabularyGroups$.pipe(takeUntilDestroyed(), distinctUntilChanged((a, b) => isEqual(a, b))).subscribe((vocabularyGroups) => {
      this.#vocabularyGroups = structuredClone(vocabularyGroups);
      this.vocabularyGroupFormsData = this.#vocabularyGroups.reduce((arr: { id: string, form: FormGroup }[], group) => {
        // Create Vocabulary Group From
        const vocabularyGroupForm = new FormGroup(
          {
            name: new FormControl(''),
          },
          {
            validators: ZodSharedValidator.validate(
              FlatModels.VocabularyGroupEntityModels.VocabularyGroupSchema.pick({
                name: true,
              })
            ),
            updateOn: 'change'
          }
        );

        // Update Values
        vocabularyGroupForm.reset(group);

        // Add To Array
        arr.push({
          id: group.id,
          form: vocabularyGroupForm
        });

        // Return Array
        return arr;
      }, []);
      this.#isSubmitted.set(false);
    });

    // Update Vocabulary Words
    this.vocabularySharedFacade.currentSelectedVocabularyWord$.pipe(takeUntilDestroyed(), distinctUntilChanged((a, b) => isEqual(a, b))).subscribe((vocabularyWords) => {
      this.vocabularyWords = vocabularyWords;
    });
  }

  onSubmit(id: string,
           form: FormGroup) {
    if (this.#isSubmitted() || form.invalid) {
      return;
    }
    this.#isSubmitted.set(true);

    const vocabularyGroup = assign(this.#vocabularyGroups.find(vg => vg.id === id), form.value);
    this.vocabularySharedFacade.requestUpdateVocabularyGroup(vocabularyGroup).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, form);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }

  addVocabularyGroup() {
    if (!this.#page) return;
    const vocabularyGroup: FlatModels.VocabularyGroupEntityModels.VocabularyGroup = {
      id: uuid(),
      name: 'Neue Gruppe',
      vocabularyWords: [],
      order: this.#vocabularyGroups?.length || 0,
      vocabulary: this.#page.id
    };
    this.vocabularySharedFacade.requestAddVocabularyGroup(this.#page.id, vocabularyGroup).subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result),
    });
  }

  trackByVocabularyGroup(index: number,
                         item: {id: string}) {
    return item.id || index;
  }

  removeVocabularyGroup(id: string) {
    if (!this.#page) return;
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.vocabulary_group_remove'),
      data: {
        label: 'global.vocabulary_group_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (this.#page && remove) {
        const vocabularyGroup = this.#vocabularyGroups.find(vg => vg.id === id);
        if (!vocabularyGroup) return;
        this.vocabularySharedFacade.requestRemoveVocabularyGroup(this.#page.id, vocabularyGroup).subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }

  addVocabularyWord(id: string) {
    // Show Permission Dialog
    this.dialogService.open(VocabularyWordDialogComponent, {
      header: this.translate.instant('global.vocabulary'),
      data: {
        vocabularyGroup: id,
      },
      dismissableMask: true,
      width: '30%',
    });
  }

  rowTrackBy(index: number,
             item: FlatModels.VocabularyWordEntityModels.VocabularyWord) {
    return item.id || index;
  }

  onRowSelect(event: TableRowSelectEvent,
              id: string) {
    const vocabularyWords = this.vocabularyWords[id];
    const vocabularyWord = vocabularyWords.find(vw => vw.id === event.data);

    // Show Permission Dialog
    this.dialogService.open(VocabularyWordDialogComponent, {
      header: this.translate.instant('global.vocabulary'),
      data: {
        vocabularyGroup: id,
        vocabularyWord
      },
      dismissableMask: true,
      width: '30%',
    });
  }
}
